import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, IconButton, Typography } from '@mui/material';
import { TimeView } from '@mui/x-date-pickers/models';
import { useTranslation } from 'react-i18next';
import { Dayjs } from 'dayjs';
import { DeleteBlackIcon } from '@components/icons/svg-icon.tsx';
import TimeRangePickerList from '@components/time-range-picker-list/time-range-picker-list.tsx';
import { TimeRangeEdge } from '@models/time-range-picker.model.ts';
import { WeekDayEnum } from '@models/digital-signage/device-group-schedules.model.ts';
import {
  MinutesRange,
  ScheduleDayPresetEnum,
  ScheduleRuleCardProps,
  ScheduleTimeStatusEnum
} from '@modules/digital-signage/model/playlist-schedule.model.ts';
import {
  SCHEDULE_DAY_PRESETS,
  SCHEDULE_MINUTES_STEP,
  WEEK_DAYS
} from '@modules/digital-signage/utils/schedule.utils.ts';
import {
  formatDaysText,
  getConflictOwners,
  getRangeErrorMessage,
  getRuleDayError,
  getRuleDayTimeline,
  getRuleRangeStatuses
} from '@modules/digital-signage/utils/schedule-status.utils.ts';
import { formatRange } from '@modules/digital-signage/utils/schedule-time.utils.ts';
import {
  getForeignSlots,
  getPresetDays,
  getRemainingDayAvailability,
  getValidDraftRanges,
  isDayOutOfRange,
  isDayUnavailable,
  isScheduleTimeDisabled
} from '@modules/digital-signage/utils/schedule-occupancy.utils.ts';
import { replaceTimeRange } from '@modules/digital-signage/utils/schedule-form.utils.ts';
import { toggleWeekDay } from '@modules/digital-signage/utils/schedule-date.utils.ts';
import WeekDayChip from '@modules/digital-signage/components/group-settings/components/playlist-schedule/components/week-day-chip/week-day-chip.tsx';
import ScheduleStatusBadge from '@modules/digital-signage/components/group-settings/components/playlist-schedule/components/schedule-status-badge/schedule-status-badge.tsx';

const ScheduleRuleCard: React.FC<ScheduleRuleCardProps> = ({
  index,
  rule,
  occupancy,
  validation,
  canDelete,
  showIndex,
  disabled,
  savedSlotKeys,
  onDaysChange,
  onRangesChange,
  onDelete
}) => {
  const { t } = useTranslation();

  const foreignSlots = useMemo(
    () => getForeignSlots(occupancy, index),
    [occupancy, index]
  );

  const dayAvailability = useMemo(
    () =>
      WEEK_DAYS.map((day) =>
        getRemainingDayAvailability(day, foreignSlots, occupancy)
      ),
    [foreignSlots, occupancy]
  );

  const ownRanges = useMemo<MinutesRange[]>(
    () => getValidDraftRanges(rule.timeRanges),
    [rule.timeRanges]
  );

  const conflictOwners = useMemo(
    () => getConflictOwners(validation?.conflicts ?? [], index, t),
    [validation, index, t]
  );

  // "No days" is shown only after the user has changed the days.
  const [isDaysTouched, setIsDaysTouched] = useState<boolean>(false);

  const isDayDisabled = (day: WeekDayEnum) =>
    isDayUnavailable(day, occupancy, dayAvailability);

  const handleDaysChange = (days: WeekDayEnum[]) => {
    setIsDaysTouched(true);
    onDaysChange(days);
  };

  const applyPreset = (preset: ScheduleDayPresetEnum) =>
    handleDaysChange(
      getPresetDays(preset, rule.days, occupancy, dayAvailability)
    );

  const dayError = getRuleDayError(validation, isDaysTouched, t);

  // Keeps the last text while the message fades out.
  const lastDayError = useRef<string>('');

  const conflictAlertRef = useRef<HTMLDivElement>(null);
  const hasConflicts = !!validation?.conflicts.length;

  useEffect(() => {
    if (hasConflicts) {
      conflictAlertRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [hasConflicts]);
  if (dayError) lastDayError.current = dayError;

  const toggleDay = (day: WeekDayEnum) =>
    handleDaysChange(toggleWeekDay(rule.days, day));

  const shouldDisableTime = (
    value: Dayjs,
    view: TimeView,
    edge: TimeRangeEdge,
    rangeIndex: number
  ) =>
    isScheduleTimeDisabled({
      value,
      view,
      edge,
      rangeIndex,
      ruleIndex: index,
      rule,
      occupancy
    });

  const rangeStatuses = getRuleRangeStatuses(
    rule,
    validation,
    occupancy,
    savedSlotKeys
  );

  const applySuggestion = (range: MinutesRange) =>
    onRangesChange(
      replaceTimeRange(
        rule.timeRanges,
        validation?.suggestionRangeIndex ?? -1,
        range
      )
    );

  return (
    <div className="flex flex-col gap-3 p-4 rounded-xl border border-(--gray-9)">
      <div className="flex items-center justify-between gap-3 min-h-7.5">
        <div className="flex items-center gap-2 min-w-0">
          {showIndex && (
            <Typography
              variant="caption"
              component="span"
              className="shrink-0 px-2 py-0.5 rounded-md bg-(--primary-100) text-(--primary)!"
            >
              {t('digital_signage_schedule_rule_number', { index: index + 1 })}
            </Typography>
          )}
          <Typography variant="body2" className="font-medium! text-(--dark-1)!">
            {t('digital_signage_schedule_days_label')}
          </Typography>
        </div>

        <div className="flex items-center gap-1">
          {Object.values(ScheduleDayPresetEnum).map((preset) => {
            const isSelected =
              SCHEDULE_DAY_PRESETS[preset].join() === rule.days.join();

            return (
              <Button
                key={preset}
                size="small"
                disabled={
                  disabled ||
                  !SCHEDULE_DAY_PRESETS[preset].some(
                    (day) => !isDayDisabled(day)
                  )
                }
                onClick={() => applyPreset(preset)}
                className={`min-w-0! h-6.5! px-2! rounded-md! ${
                  isSelected
                    ? 'bg-(--primary-100)! text-(--primary)!'
                    : 'text-(--blue-4)! hover:text-(--primary)!'
                }`}
              >
                <Typography
                  variant="caption"
                  component="span"
                  className="font-medium! text-inherit!"
                >
                  {t(`digital_signage_schedule_preset_${preset}`)}
                </Typography>
              </Button>
            );
          })}

          {canDelete && (
            <IconButton
              aria-label={t('digital_signage_schedule_remove_rule')}
              disabled={disabled}
              onClick={onDelete}
              color="error"
              className="shrink-0 w-7.5! h-7.5! ml-1! bg-(--gray-11)! hover:bg-(--gray-10)!"
            >
              <DeleteBlackIcon className="w-4! h-4!" />
            </IconButton>
          )}
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {WEEK_DAYS.map((day) => (
          <WeekDayChip
            key={day}
            day={day}
            selected={rule.days.includes(day)}
            hasConflict={!!validation?.conflictDays.includes(day)}
            isOutOfRange={isDayOutOfRange(day, occupancy)}
            status={getRuleDayTimeline(
              day,
              rule.days,
              ownRanges,
              occupancy,
              savedSlotKeys
            )}
            disabled={disabled}
            availability={dayAvailability[day]}
            ownRanges={ownRanges}
            onToggle={toggleDay}
          />
        ))}
      </div>

      {/* The line keeps a fixed height, so the message fades in/out without a jump. */}
      <Typography
        variant="caption"
        component="p"
        className={`!-mt-2 !-mb-2 h-4.5 leading-4.5! text-(--red-2)! transition-all duration-300 ease-in-out ${
          dayError
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 -translate-y-1 pointer-events-none'
        }`}
      >
        {dayError || lastDayError.current}
      </Typography>

      {/* Stretch both pickers so the row fills the card width. */}
      <div className="[&_.MuiFormControl-root]:flex-1 [&_.MuiFormControl-root]:min-w-0">
        <TimeRangePickerList
          mode="multiple"
          required
          label="digital_signage_schedule_time_label"
          tooltip="digital_signage_schedule_time_tooltip"
          updateData={rule.timeRanges}
          onChange={onRangesChange}
          minutesStep={SCHEDULE_MINUTES_STEP}
          emitEmptyRanges
          sortRanges={false}
          rangeErrors={validation?.ranges.map(
            (range) =>
              range.isInvalid || range.isPast || range.conflicts.length > 0
          )}
          rangeMessages={validation?.ranges.map((range, rangeIndex) =>
            getRangeErrorMessage(range, rule.timeRanges[rangeIndex], t)
          )}
          shouldDisableTime={shouldDisableTime}
          disabled={disabled}
          disabledRanges={rangeStatuses.map(
            (status) => status === ScheduleTimeStatusEnum.Ended
          )}
          rangeAdornments={rangeStatuses.map((status) =>
            status ? <ScheduleStatusBadge status={status} /> : null
          )}
        />
      </div>

      {hasConflicts && (
        <Alert
          ref={conflictAlertRef}
          severity="warning"
          className="items-start! rounded-xl! scroll-mb-28 [&_.MuiAlert-message]:w-full"
        >
          <Typography variant="body2" className="font-semibold!">
            {t('digital_signage_schedule_conflict_title')}
          </Typography>
          <Typography
            variant="caption"
            component="p"
            className="mt-1 leading-5! text-(--dark-alpha-800)!"
          >
            {t('digital_signage_schedule_conflict_description', {
              owners: conflictOwners.join(', '),
              days: formatDaysText(validation.conflictDays, t)
            })}
          </Typography>

          {validation.suggestions.length > 0 && (
            <div className="flex items-center flex-wrap gap-1.5 mt-2">
              <Typography
                variant="caption"
                className="text-(--dark-alpha-800)!"
              >
                {t('digital_signage_schedule_free_slots')}
              </Typography>
              {validation.suggestions.map((range) => (
                <Button
                  key={`${range.start}-${range.end}`}
                  size="small"
                  variant="outlined"
                  onClick={() => applySuggestion(range)}
                  className="min-w-0! h-7! px-3! rounded-full! bg-(--white)! border-(--primary)/35! text-(--primary)!"
                >
                  <Typography
                    variant="caption"
                    component="span"
                    className="font-medium! text-inherit!"
                  >
                    {formatRange(range, t)}
                  </Typography>
                </Button>
              ))}
            </div>
          )}
        </Alert>
      )}
    </div>
  );
};

export default ScheduleRuleCard;
